import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { API } from '../../data/API/API';
import Datalist, { ListColumn } from '../Datalist/Datalist';
import './ReviewListModal.scss';

interface ReviewListModalProps {
    orgNr: string;
    isOpen: boolean;
    onClose: () => void;
}

interface Review {
    created: string;
    createdBy: string;
    period: string | null;
    endPeriod: string | null;  // New field for EndPeriod
}

const ReviewListModal: React.FC<ReviewListModalProps> = ({ orgNr, isOpen, onClose }) => {
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            const fetchReviews = async () => {
                setLoading(true);
                try {
                    const response = await API.Suppliers.GetAllReviewsBySupplierOrgId(orgNr);
                    console.log("Fetched Reviews:", response);
                    setReviews(response || []);
                } catch (err) {
                    setError('Failed to fetch reviews');
                } finally {
                    setLoading(false);
                }
            };
            fetchReviews();
        }
    }, [orgNr, isOpen]);

    // Define the columns for the Datalist table
    const columns: ListColumn<Review>[] = React.useMemo(() => [
        {
            key: 'created',
            fieldName: 'created',
            name: 'Datum',
            minWidth: 150,
            isActive: true,
            sorting: true,
            onRender: (item: Review) => item.created,
            footer: (items: Review[]) => `Totalt antal rader: ${items.length}`,
        },
        {
            key: 'createdBy',
            fieldName: 'createdBy',
            name: 'Skapad Av',
            minWidth: 150,
            isActive: true,
            sorting: true,
            onRender: (item: Review) => item.createdBy,
        },
        {
            key: 'period',
            fieldName: 'period',
            name: 'Period',
            minWidth: 150,
            isActive: true,
            sorting: true,
            onRender: (item: Review) => item.period || 'N/A',
        },
        {
            key: 'endPeriod',
            fieldName: 'endPeriod',
            name: 'End Period',
            minWidth: 150,
            isActive: true,
            sorting: true,
            onRender: (item: Review) => item.endPeriod || 'N/A', // Display 'N/A' if endPeriod is null
        }
    ], []);

    return (
        <Modal show={isOpen} onHide={onClose} centered dialogClassName="custom-modal-width">
            <Modal.Header closeButton>
                <Modal.Title>Granskningar för Leverantören</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <Spinner animation="border" />
                ) : error ? (
                    <p>{error}</p>
                ) : (
                    <div className="review-list" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        {reviews.length > 0 ? (
                            <Datalist
                                items={reviews}
                                isLoading={false}
                                columns={columns}
                                setColumns={(updatedColumns: ListColumn<Review>[]) => {
                                    console.log('Columns updated:', updatedColumns);
                                }}
                                initialSortKey="Created"
                                descendingSort={true}
                                noItemsText="Inga granskningar tillgängliga"
                                uniqueKey="Created"
                            />
                        ) : (
                            <p>Ingen data tillgänglig</p>
                        )}
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ReviewListModal;
